import capitalize from "lodash/capitalize";
import isEqual from "lodash/isEqual";
import {
  PARAMETER_OPTIONS_ARRAY,
  PARAMETER_OPTIONS,
  OUTCOME_STATUS,
} from "../constants";
import convertToRiR from "./converters/convert-intensity";
import { repsValue } from "./reps-range";
import { getLoadDrop } from "./load-drop";

const { FREEFORM, REPS, LOAD, RPEAMRAP, SETS } = PARAMETER_OPTIONS;
const { COMPLETED, MISSED } = OUTCOME_STATUS;

/**
 * Used to determine if any individual set of an exercise is marked completed/missed
 * and actual results should be shown
 *
 * @param exercise {Object} Exercise to evaluate
 * @returns {Boolean} true if any individual set is completed/missed, false otherwise
 */
export const anySetCompleted = (exercise) => {
  const sets = exercise.sets;
  for (const element of sets) {
    if (element.outcomes && Object.keys(element.outcomes).length > 0) {
      // Return and stop looping if any set has an outcome
      return true;
    }
  }
  return false;
};

/**
 * Returns the short-form target for a given exercise,
 * for example: "3 x 6 Reps @ 80kg (50%)" for a powerlifting exercise,
 * or "4 x 30 Time | 5 Distance | 3 Rest" for an endurance exercise
 *
 * @param values {Object} Set object
 * @param powerlifting {Boolean} Whether the exercise is a powerlifting exercise
 * @param percentRM {String} Intensity % to show if it exists
 * @param units {String} User's units (metric | imperial)
 * @returns {String} Short-form target string
 */
export const formattedTarget = (
  values,
  powerlifting,
  percentRM,
  units,
  load,
  intensityMeasure = "RPE",
  isRiR = false,
  capitalizeRPE = false
) => {
  if (!values) {
    return "-";
  }

  const { sets, reps, rpe, freeform, time, rounds, repsLower, loadDrop } =
    values;

  if (powerlifting) {
    const setsReps = `${sets} x ${
      rpe === "AMRAP" ? rpe : repsValue(reps, repsLower)
    } ${rpe === "AMRAP" ? "" : reps > 1 ? "Reps" : "Rep"}`;
    const rpeStr =
      rpe && rpe !== "AMRAP"
        ? ` @ ${intensityMeasure}${convertToRiR(rpe, !isRiR)}`
        : "";
    const loadStr =
      load || loadDrop
        ? ` ${rpe ? "|" : "@"} ${loadDrop ? getLoadDrop(loadDrop) : load}`
        : "";
    let intensity = percentRM && !isNaN(percentRM) ? ` (${percentRM}%)` : "";
    if (loadDrop) intensity = "";
    const rpeStrToDisplay =
      capitalizeRPE && rpeStr.length > 0 && rpeStr.includes("rpe")
        ? rpeStr.replace("rpe", "RPE")
        : capitalizeRPE && rpeStr.length > 0 && rpeStr.includes("rir")
        ? rpeStr.replace("rir", "RiR")
        : rpeStr;

    return setsReps + rpeStrToDisplay + loadStr + intensity;
  }

  if (freeform) {
    return freeform;
  }

  const filtered = Object.keys(values).filter((val) => {
    const capitalized = capitalize(val);
    return PARAMETER_OPTIONS_ARRAY.includes(capitalized);
  });

  let formattedTarget = filtered.reduce((str, key, i) => {
    if (
      ["rounds", "time", "sets", "reps", "rpe", "load", "velocity"].includes(
        key
      )
    ) {
      return str;
    }

    return `${str} ${values[key]} ${capitalize(key)}${
      i < filtered.length - 1 ? " | " : ""
    }`;
  }, "");

  if (loadDrop) {
    formattedTarget = `${rpe ? "|" : "@"} ${getLoadDrop(
      loadDrop
    )} ${formattedTarget}`;
  } else if (load) {
    formattedTarget = `${rpe ? "|" : "@"} ${load} ${formattedTarget}`;
  }

  if (rpe) {
    formattedTarget = `${
      rpe === "AMRAP" ? "" : `@ ${intensityMeasure}`
    }${convertToRiR(rpe, !isRiR)} ${formattedTarget}`;
  }

  if (time) {
    formattedTarget = formattedTarget.length
      ? `${time} Time | ${formattedTarget}`
      : `${time} Time`;
  } else if (reps && rpe !== "AMRAP") {
    formattedTarget = `${repsValue(reps, repsLower)} ${
      reps > 1 ? "Reps" : "Rep"
    } ${formattedTarget}`;
  }

  if (rounds) {
    formattedTarget = `${rounds} x ${formattedTarget}`;
  } else if (sets) {
    formattedTarget = `${sets} x ${formattedTarget}`;
  }

  if (capitalizeRPE) {
    const formattedTargetToDisplay =
      formattedTarget.length > 0 && formattedTarget.includes("rpe")
        ? formattedTarget.replace("rpe", "RPE")
        : formattedTarget.length > 0 && formattedTarget.includes("rir")
        ? formattedTarget.replace("rir", "RiR")
        : formattedTarget;
    return formattedTargetToDisplay;
  }

  return formattedTarget;
};

/**
 * Used to get the total, completed and missed set counts for an exercise set
 *
 * @param exercise {Object} Exercise to evaluate
 * @param set {Object} Exercise set to evaluate
 * @returns {Object} Object with structure: { total: Number, completed: Number, missed: Number }
 */
export const getSetCounts = (exercise, set) => {
  let total = 0;
  let completed = 0;
  let missed = 0;
  // Any exercise that isn't Free-Form
  if (
    !exercise.parameters ||
    (exercise.parameters && exercise.parameters[0] !== FREEFORM)
  ) {
    total = Number(set.sets || set.rounds || 1);
    if (set.outcomes && Object.keys(set.outcomes).length > 0) {
      Object.keys(set.outcomes).forEach((key) => {
        if (set.outcomes[key].status === COMPLETED) {
          completed += 1;
        } else if (set.outcomes[key].status === MISSED) {
          missed += 1;
        }
      });
    }
  }
  // Free-Form exercise
  if (exercise.parameters && exercise.parameters[0] === FREEFORM) {
    total = 1;
    if (!!set.outcomes && !!set.outcomes[0]) {
      if (set.outcomes && set.outcomes[0].status === COMPLETED) {
        completed += 1;
      } else if (set.outcomes && set.outcomes[0].status === MISSED) {
        missed += 1;
      }
    }
  }
  return { total, completed, missed };
};

/**
 * Determine if the exercise should have e1RM value (true for powerlifting movements
 * and assistant type that has reps and load in parameters)
 *
 * @param exercise {Object} Exercise to evaluate
 * @returns {Boolean} true if exercise should have e1RM, false otherwise
 */
export const shouldHaveE1RM = (exercise) => {
  if (!exercise.parameters) {
    // Powerlifting movement (sets, reps, rpe, load) - should have e1RM
    return true;
  } else if (
    exercise.parameters &&
    exercise.parameters.includes(REPS) &&
    exercise.parameters.includes(LOAD)
  ) {
    // Movement with parameters including reps and load - should have e1RM
    return true;
  }

  return false;
};

/**
 * Used to get the total, completed and missed set counts for all sets in an exercise
 *
 * @param exercise {Object} Exercise to evaluate
 * @returns {Object} Object with structure: { total: Number, completed: Number, missed: Number }
 */
export const getAllSetsCounts = (exercise) => {
  let total = 0;
  let completed = 0;
  let missed = 0;

  if (!exercise.sets || !exercise.sets.length) {
    return { total, completed, missed };
  }

  exercise.sets.forEach((set) => {
    total += Number(set.sets || set.rounds || 1);
    if (set.outcomes && Object.keys(set.outcomes).length > 0) {
      Object.keys(set.outcomes).forEach((key) => {
        if (set.outcomes[key].status === COMPLETED) {
          completed += 1;
        } else if (set.outcomes[key].status === MISSED) {
          missed += 1;
        }
      });
    }
  });

  return { total, completed, missed };
};

/**
 * Determine if the exercise has RPE
 *
 * @param exercise {Object} Exercise to evaluate
 * @returns {Boolean} true if exercise has RPE, false otherwise
 */
export const hasRpe = (exercise) => {
  if (!exercise.parameters) {
    // Powerlifting movements have RPE
    return true;
  } else if (exercise.parameters && exercise.parameters.includes(RPEAMRAP)) {
    // Movement with RPE in parameters
    return true;
  }

  return false;
};

/**
 * Determine if the exercise has Sets and Reps
 *
 * @param exercise {Object} Exercise to evaluate
 * @returns {Boolean} true if exercise has Sets and Reps, false otherwise
 */
export const hasSetsReps = (exercise) => {
  if (!exercise.parameters) {
    // Powerlifting movements have Sets and Reps
    return true;
  } else if (
    exercise.parameters &&
    exercise.parameters.includes(SETS) &&
    exercise.parameters.includes(REPS)
  ) {
    // Movement with Sets and Reps in parameters
    return true;
  }

  return false;
};

/**
 * Get number of lifts, sets and volume for an exercise
 * @param {Object} exercise - exercise object
 * @returns {Object} Object with lifts, sets, volume, rpeVolume, missedSets of an exercise
 */
export const getLiftsSetsVolume = (exercise) => {
  let lifts = 0,
    sets = 0,
    volume = 0,
    rpeVolume = 0,
    missedSets = 0;

  exercise.sets.forEach((set) => {
    if (set.outcomes) {
      Object.entries(set.outcomes).forEach(([k, { outcome, status }]) => {
        if (status === COMPLETED) {
          const { load, reps, rpe } = outcome || {};
          lifts += Number(reps) || 0;
          sets++;
          volume += Math.round(Number(load) * Number(reps) || 0);
          if (Number(rpe))
            rpeVolume += Math.round(Number(load) * Number(reps) || 0);
        } else if (status === MISSED) {
          missedSets++;
        }
      });
    }
  });

  return { lifts, sets, volume, rpeVolume, missedSets };
};

/**
 * Get the weighted intensity for an exercise
 * @param {Object} exercise - exercise object
 * @param {Number} volume - volume of the exercise
 * @returns {Number} weighted intensity of a given exercise
 */
export const getWeightedIntensity = (exercise, volume) => {
  if (!volume) return 0;

  const { primary, repMaximums, sets, secondaryRepMaximum, type } = exercise;
  const repMax =
    type === "secondary" && secondaryRepMaximum
      ? secondaryRepMaximum
      : repMaximums[primary] || 0;
  let weightedIntensity = 0;

  sets.forEach((set) => {
    if (set.outcomes) {
      Object.entries(set.outcomes).forEach(([k, { outcome, status }]) => {
        if (status === COMPLETED) {
          const load = Number(outcome?.load || 0);
          const reps = Number(outcome?.reps || 0);
          weightedIntensity += (load / repMax) * ((load * reps) / volume) || 0;
        }
      });
    }
  });

  return weightedIntensity;
};

/**
 * Get the weighted RPE for an exercise
 * @param {Object} exercise - exercise object
 * @param {Number} volume - volume of the exercise
 * @returns {Number} weighted RPE of a given exercise
 */
export const getWeightedRpe = (exercise, volume) => {
  if (!volume) return 0;

  let weightedRpe = 0;

  exercise.sets.forEach((set) => {
    if (set.outcomes) {
      Object.entries(set.outcomes).forEach(([k, { outcome, status }]) => {
        if (status === COMPLETED) {
          const { rpe, reps, load } = outcome;
          weightedRpe +=
            Number(rpe) * ((Number(load) * Number(reps)) / volume) || 0;
        }
      });
    }
  });

  return weightedRpe;
};

/**
 * Get the highest e1rm, intensity and rpe values for an exercise
 * @param {Object} exercise - exercise object
 * @returns {Object} Object with e1rm, intensity and rpe (peak of exercise)
 */
export const getPeakValues = (exercise) => {
  const { primary, repMaximums, sets, secondaryRepMaximum, type } = exercise;
  const repMax =
    type === "secondary" && secondaryRepMaximum
      ? secondaryRepMaximum
      : repMaximums[primary] || 0;

  return sets
    .map((set) => {
      if (set.outcomes) {
        return Object.entries(set.outcomes).reduce(
          (prev, [k, { status, outcome }]) => {
            if (status !== COMPLETED) return prev;
            if (status === COMPLETED && !outcome) return prev;
            const intensity = outcome.load / repMax;
            const rpe = Number(outcome.rpe);
            return {
              e1rm: outcome.e1rm > prev.e1rm ? outcome.e1rm : prev.e1rm,
              intensity:
                intensity > prev.intensity ? intensity : prev.intensity,
              rpe: rpe > prev.rpe ? rpe : prev.rpe,
            };
          },
          { e1rm: 0, intensity: 0, rpe: 0 }
        );
      }
      return { e1rm: 0, intensity: 0, rpe: 0 };
    })
    .reduce(
      (prev, { e1rm, intensity, rpe }) => {
        return {
          e1rm: e1rm > prev.e1rm ? e1rm : prev.e1rm,
          intensity: intensity > prev.intensity ? intensity : prev.intensity,
          rpe: rpe > prev.rpe ? rpe : prev.rpe,
        };
      },
      { e1rm: 0, intensity: 0, rpe: 0 }
    );
};

/**
 * Check if all the outcomes of a set are equal
 * @param {Object} set - set object
 * @returns {Boolean} true if all outcomes are equal, false otherwise
 */
export const outcomesAreEqual = (set) => {
  if (
    !set.outcomes ||
    (!set.sets && !set.rounds) ||
    set.sets == 1 ||
    set.rounds == 1
  ) {
    return false;
  }

  for (let i = 1; i < Number(set.sets || set.rounds); i++) {
    const prev = set.outcomes[i - 1];
    const current = set.outcomes[i];
    if (!isEqual(prev, current)) return false;
  }

  return true;
};

/**
 * Check if one of the exercise's sets has equal outcomes
 * @param {Object} exercise - exercise object
 * @returns {Boolean} true if the exercise has a set with equal outcomes, false otherwise
 */
export const hasEqualOutcomes = (exercise) => {
  for (const set of exercise.sets) {
    let setIsEqual = true;
    if (
      !set.outcomes ||
      (!set.sets && !set.rounds) ||
      set.sets == 1 ||
      set.rounds == 1
    ) {
      continue;
    }

    for (let j = 1; j < Number(set.sets || set.rounds); j++) {
      const prev = set.outcomes[j - 1];
      const current = set.outcomes[j];
      if (!isEqual(prev, current)) setIsEqual = false;
    }
    // One of the exercise's sets has equalOutcomes
    if (setIsEqual) return true;
  }
  return false;
};

/**
 * Get number of outcomes, videos and comments for a set
 * @param {Object} set - set object
 * @returns {Object} object with outcomeCount, commentCount, videoCount
 */
export const getMetaCounts = (set) => {
  const { outcomes, comments, videos } = set;
  const outcomeCount = (outcomes && Object.keys(outcomes).length) || 0;
  const commentCount = (comments && Object.keys(comments).length) || 0;
  const videoCount = (videos && Object.keys(videos).length) || 0;

  return { outcomeCount, commentCount, videoCount };
};

/**
 * Get velocity points for an specified parameter
 * @param {Array} sets - sets array
 * @param {param} string - specified parameter | load | rpe
 * @return {Array} array of velocity values [ [parameter, velocity], ... ]
 */
export const getVelocityDataFromParam = (
  sets,
  param,
  fromMetric,
  units,
  isRiR
) => {
  let result = [];

  sets.forEach((set) => {
    return (
      set.outcomes &&
      Object.values(set.outcomes).forEach((singleOutcome) => {
        if (
          singleOutcome.outcome?.[param] &&
          singleOutcome.outcome?.velocity &&
          ((singleOutcome.outcome?.velocity.simple &&
            singleOutcome.outcome?.velocity.simple.trim() !== "") ||
            (singleOutcome.outcome?.velocity?.detailed &&
              Object.keys(singleOutcome.outcome?.velocity.detailed).length >
                0 &&
              Object.values(singleOutcome.outcome?.velocity.detailed).some(
                (value) => Number(value) > 0
              )))
        ) {
          // Get the last outcome with a velocity value
          const { velocity } = singleOutcome.outcome;

          const averageSetVelocity = getFirstValue(velocity?.detailed);

          const hasDetailedData = Boolean(averageSetVelocity);

          let value = 0;

          if (param === "load") {
            value = fromMetric(singleOutcome.outcome[param], units, false);
          } else {
            value = convertToRiR(singleOutcome.outcome[param], !isRiR);
          }

          result.push([
            value,
            hasDetailedData
              ? Number(averageSetVelocity)
              : Number(velocity.simple),
          ]);
        }
      })
    );
  });

  if (result.length > 0) {
    return result;
  }

  return null;
};

/**
 * @param {Object} velocity - the velocity detailed object
 * @returns {Number} - The first value of the object
 */
export const getFirstValue = (velocity) => {
  if (!velocity) {
    return 0;
  }

  const keys = Object.keys(velocity);

  if (keys.length === 0) {
    return 0;
  }

  // Sort the keys in ascending order
  keys.sort((a, b) => parseInt(a) - parseInt(b));

  // Use the first key as the calculated startIndex
  const startIndex = parseInt(keys[0]);

  // Return the corresponding value for the calculated startIndex
  // This is done in case the user's inputted first rep is the second or so
  return velocity[startIndex.toString()] || 0;
};

/**
 * Get velocity value, either simple or first value of detailed
 * @param {Object} velocity - the velocity object
 * @returns {Number} - The velocity value
 */
export const getVelocityValue = (velocity) => {
  if (!velocity) return 0;

  const simpleValue = velocity?.simple || 0;

  const averageSetVelocity = getFirstValue(velocity?.detailed);

  const hasDetailedData = Boolean(averageSetVelocity);

  return hasDetailedData ? averageSetVelocity : simpleValue;
};
